export interface KMeansPoint<TPayload = Record<string, unknown>> {
  id: string;
  label: string;
  vector: number[];
  payload: TPayload;
}

export interface KMeansCluster<TPayload = Record<string, unknown>> {
  id: string;
  centroid: number[];
  points: Array<KMeansPoint<TPayload>>;
  sse: number;
}

export interface KMeansResult<TPayload = Record<string, unknown>> {
  clusters: Array<KMeansCluster<TPayload>>;
  iterations: number;
  converged: boolean;
  totalSse: number;
}

const DEFAULT_MAX_ITERATIONS = 50;
const CONVERGENCE_TOLERANCE = 1e-6;

function getDistance(left: number[], right: number[]) {
  return Math.sqrt(
    left.reduce((sum, value, index) => {
      const delta = value - right[index];
      return sum + delta * delta;
    }, 0),
  );
}

function getCentroid(vectors: number[][]) {
  if (!vectors.length) {
    return [];
  }

  return vectors[0].map((_, dimensionIndex) => {
    const total = vectors.reduce((sum, vector) => sum + vector[dimensionIndex], 0);
    return total / vectors.length;
  });
}

function initializeCentroids<TPayload>(points: Array<KMeansPoint<TPayload>>, clusterCount: number) {
  const sortedPoints = [...points].sort((left, right) => {
    const leftMagnitude = left.vector.reduce((sum, value) => sum + value * value, 0);
    const rightMagnitude = right.vector.reduce((sum, value) => sum + value * value, 0);
    return leftMagnitude - rightMagnitude;
  });

  if (clusterCount === 1) {
    return [sortedPoints[0].vector.slice()];
  }

  return Array.from({ length: clusterCount }, (_, index) => {
    const pointIndex = Math.round((index * (sortedPoints.length - 1)) / (clusterCount - 1));
    return sortedPoints[pointIndex].vector.slice();
  });
}

export function normalizeVectors(vectors: number[][]) {
  if (!vectors.length) {
    return [];
  }

  const dimensionCount = vectors[0].length;

  return vectors.map((vector) =>
    vector.map((value, dimensionIndex) => {
      const valuesForDimension = vectors.map((candidate) => candidate[dimensionIndex]);
      const minimum = Math.min(...valuesForDimension);
      const maximum = Math.max(...valuesForDimension);

      if (maximum === minimum) {
        return dimensionCount === 0 ? 0 : 0.5;
      }

      return (value - minimum) / (maximum - minimum);
    }),
  );
}

export function runKMeans<TPayload>(
  points: Array<KMeansPoint<TPayload>>,
  requestedClusterCount: number,
  maxIterations = DEFAULT_MAX_ITERATIONS,
): KMeansResult<TPayload> {
  if (!points.length) {
    return {
      clusters: [],
      iterations: 0,
      converged: true,
      totalSse: 0,
    };
  }

  const clusterCount = Math.max(1, Math.min(requestedClusterCount, points.length));
  let centroids = initializeCentroids(points, clusterCount);
  let assignments = new Array(points.length).fill(0);
  let converged = false;
  let iterations = 0;

  while (iterations < maxIterations && !converged) {
    const nextAssignments = points.map((point) => {
      let bestClusterIndex = 0;
      let bestDistance = Number.POSITIVE_INFINITY;

      centroids.forEach((centroid, centroidIndex) => {
        const distance = getDistance(point.vector, centroid);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestClusterIndex = centroidIndex;
        }
      });

      return bestClusterIndex;
    });

    const nextCentroids = centroids.map((centroid, centroidIndex) => {
      const vectors = points
        .filter((_, pointIndex) => nextAssignments[pointIndex] === centroidIndex)
        .map((point) => point.vector);

      return vectors.length ? getCentroid(vectors) : centroid;
    });

    converged = nextCentroids.every((centroid, centroidIndex) => {
      return getDistance(centroid, centroids[centroidIndex]) <= CONVERGENCE_TOLERANCE;
    });

    assignments = nextAssignments;
    centroids = nextCentroids;
    iterations += 1;
  }

  const clusters = centroids.map((centroid, centroidIndex) => {
    const clusterPoints = points.filter((_, pointIndex) => assignments[pointIndex] === centroidIndex);
    const sse = clusterPoints.reduce((sum, point) => {
      const distance = getDistance(point.vector, centroid);
      return sum + distance * distance;
    }, 0);

    return {
      id: `cluster-${centroidIndex + 1}`,
      centroid,
      points: clusterPoints,
      sse,
    };
  });

  return {
    clusters,
    iterations,
    converged,
    totalSse: clusters.reduce((sum, cluster) => sum + cluster.sse, 0),
  };
}